import {
  ApeironBlueButton,
  NextLink,
  Pagination,
  useMessageContext
} from '@apeiron/temp'
import { Box, styled } from '@mui/material'
import GuildCard from '@src/components/guild/change/GuildCard'
import ApeironScrollBox from '@src/components/share/apeiron/ApeironScrollBox'
import ENV_CONFIG from '@src/config'
import { useGuildChangeContext } from '@src/contexts/guild/GuildChangeContext'
import { Guild } from '@src/types/guild'
import * as R from 'ramda'
import { FC, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const Container = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow: hidden;
  padding-bottom: 30px;
`

const List = styled(ApeironScrollBox)`
  --border-radius: 2px;
  --scrollbar-color: #ffffff88;
  column-gap: 24px;
  display: flex;
  flex-wrap: wrap;
  flex: 1;
  justify-content: center;
  overflow-y: auto;
  row-gap: 12px;
`

const RegisterCommunity = styled(NextLink)`
  align-self: flex-end;
  font-size: 16px;
  font-weight: 300;
  text-decoration: underline;
`

const Bottom = styled(Box)`
  display: flex;
  justify-content: space-between;
  width: 100%;
`

const OkButton = styled(ApeironBlueButton)`
  height: 36px;
  width: 140px;
`

const GuildSelectList: FC<Props> = (props: Props) => {
  const { className, onConfirm } = props

  const {
    myGuild,
    guilds,
    guildsPageInfo,
    selectedGuild,
    jumpToPage,
    setSelectedGuild
  } = useGuildChangeContext()

  const { setInfo } = useMessageContext()

  const { t } = useTranslation()

  const handleOnCardClick = useCallback(
    (guild: Guild) => {
      if (guild.id === selectedGuild?.id || guild.id === myGuild?.id) {
        setSelectedGuild(null)
      } else {
        setSelectedGuild(guild)
      }
    },
    [myGuild?.id, selectedGuild?.id, setSelectedGuild]
  )

  const handleOnPreviousPage = useCallback(() => {
    jumpToPage(guildsPageInfo.page - 1)
  }, [guildsPageInfo.page, jumpToPage])

  const handleOnNextPage = useCallback(() => {
    jumpToPage(guildsPageInfo.page + 1)
  }, [guildsPageInfo.page, jumpToPage])

  const handleOnSubmitPage = useCallback(
    (page: number) => {
      jumpToPage(page)
    },
    [jumpToPage]
  )

  const handleOnConfirm = useCallback(() => {
    if (onConfirm && !R.isNil(selectedGuild)) {
      onConfirm(selectedGuild)
    }
  }, [onConfirm, selectedGuild])

  const handleOnOk = useCallback(() => {
    setInfo({
      cancel: t('common.button.cancel'),
      confirm: t('common.button.confirm'),
      message: t('guild.change_confirm'),
      onConfirm: handleOnConfirm
    })
  }, [handleOnConfirm, setInfo, t])

  return (
    <Container className={className}>
      <List>
        {R.map((guild: Guild) => {
          const selected = guild.id === selectedGuild?.id

          const joined = guild.id === myGuild?.id

          return (
            <GuildCard
              guild={guild}
              joined={joined}
              key={guild.id}
              selected={selected}
              onClick={handleOnCardClick}
            />
          )
        }, guilds)}
      </List>
      <RegisterCommunity
        href={ENV_CONFIG.THIRD_PARTY_API_URL.COMMUNITY_REGISTER}
        target='_blank'
      >
        {`${t('guild.register_new')} >>`}
      </RegisterCommunity>
      <Bottom>
        <Pagination
          nextDisabled={R.isNil(guildsPageInfo.nextCursor)}
          prevDisabled={guildsPageInfo.page === 1}
          value={guildsPageInfo.page}
          onNext={handleOnNextPage}
          onPrev={handleOnPreviousPage}
          onSubmit={handleOnSubmitPage}
        />
        <OkButton onClick={handleOnOk} disabled={R.isNil(selectedGuild)}>
          {t('common.button.ok')}
        </OkButton>
      </Bottom>
    </Container>
  )
}

type Props = {
  className?: string
  onConfirm?: (guild: Guild) => void
}

export default GuildSelectList
